import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useFloraPage } from '../../api/flora-pages-client/flora-pages-client.hooks';
import { icons } from '../../assets/icons/icons';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useResizableWidth } from '../../hooks/useResizableWidth';
import { useGlobalStore } from '../../store/global-store/global-store';
import { Icon } from '../../ui-library/icon/icon';
import { FloraAside } from '../flora/flora-aside';
import { buildPageContextPreamble } from '../flora/flora-page-context-preamble';
import { FloraResizeHandle } from '../flora/components/flora-resize-handle';
import { FLORA_ASIDE_MAX_WIDTH_RATIO, FLORA_ASIDE_MIN_WIDTH } from '../flora/flora.type';
import { FloraPageEmbed, type FloraPageEmbedHandle } from './components/flora-page-embed';
import { FloraPageVisibilityMenu } from './components/flora-page-visibility-menu';
import { useFloraPageActions } from './components/use-flora-page-actions';

/**
 * Full-window viewer for a published Flora page — no app chrome, the page IS
 * the screen (like opening the artifact itself), with only a small floating
 * toolbar for back/reload. The sandboxed iframe and signed-URL lifecycle live
 * in the shared `FloraPageEmbed`; this route adds the full-window chrome and
 * the owner-only visibility control. Reload refetches a fresh signed URL via
 * the embed's imperative handle.
 *
 * @returns {JSX.Element} The page viewer route.
 */
export function FloraPageViewer() {
  // Priority 1 so this outranks the `Flora - Bloomfilter` fallback claimed by
  // FloraAgentLayout, which renders this view through its outlet.
  useDocumentTitle('Page - Flora - Bloomfilter', 1);
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const { page } = useFloraPage(pageId);
  const { isOwner, toggleVisibility } = useFloraPageActions();
  const embedRef = useRef<FloraPageEmbedHandle>(null);
  const clipboard = useClipboard({ timeout: 1500 });
  const useAI = useGlobalStore((state) => state.useAI);
  const [isFloraOpen, setIsFloraOpen] = useState(false);
  // Once opened, keep the chat pane mounted so closing it is a reversible
  // toggle: the thread id, messages, and WebSocket owned by FloraAside survive a
  // close→reopen instead of being torn down and restarted as a blank chat. We
  // defer the first mount until the user opens it so no socket is opened eagerly.
  const [hasOpenedFlora, setHasOpenedFlora] = useState(false);
  useEffect(() => {
    if (isFloraOpen) setHasOpenedFlora(true);
  }, [isFloraOpen]);

  // Right-docked resize: the pane ends at the viewport's right edge, so the
  // candidate width is the distance from the cursor to that edge. Capped at the
  // same viewport fraction as the in-app Flora aside for visual parity.
  const {
    width: floraPaneWidth,
    maxWidth: floraPaneMaxWidth,
    isResizing: isResizingFlora,
    handleMouseDown: startFloraResize,
    setWidth: setFloraPaneWidth,
  } = useResizableWidth({
    initialWidth: FLORA_ASIDE_MIN_WIDTH,
    minWidth: FLORA_ASIDE_MIN_WIDTH,
    maxWidth: () => Math.round(window.innerWidth * FLORA_ASIDE_MAX_WIDTH_RATIO),
    computeWidth: (event) => window.innerWidth - event.clientX,
  });

  // Seeds the docked Flora chat with the page the user is viewing. Passed as
  // FloraAside's `contextPreamble`, which prepends it to the first message only.
  // A page has no place in the portfolio-object `contextualInfo` model, so this
  // message preamble is how Flora learns which page is on screen.
  const floraContextPreamble = pageId ? buildPageContextPreamble(pageId, page?.title) : undefined;

  useEffect(() => {
    if (clipboard.error) {
      notifications.show({ message: 'Could not copy the link. Please try again.', color: 'red' });
    }
  }, [clipboard.error]);

  const handleCopyLink = () => {
    if (!pageId) return;
    clipboard.copy(`${window.location.origin}/application/flora-agent/pages/${pageId}`);
  };

  return (
    <FullWindow>
      <PagePane>
        <FloatingToolbar>
          <ToolbarButton
            type="button"
            aria-label="Back to pages"
            title="Back to pages"
            onClick={() => navigate('/application/flora-agent/pages')}
          >
            <Icon name="arrow_back" size={18} />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            aria-label="Reload page"
            title="Reload page"
            onClick={() => embedRef.current?.reload()}
          >
            <Icon name="refresh" size={18} />
          </ToolbarButton>
          {pageId && (
            <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy link'} withinPortal={false}>
              <ToolbarButton type="button" aria-label="Copy link" onClick={handleCopyLink}>
                <Icon name={clipboard.copied ? 'check' : 'link'} size={18} />
              </ToolbarButton>
            </Tooltip>
          )}
        </FloatingToolbar>
        {(useAI || (page && isOwner(page))) && (
          <TopRightControls>
            {page && isOwner(page) && (
              <VisibilityPill>
                <FloraPageVisibilityMenu page={page} onToggleVisibility={toggleVisibility} />
              </VisibilityPill>
            )}
            {useAI && (
              <FloraFab
                type="button"
                aria-label={isFloraOpen ? 'Close Flora' : 'Ask Flora'}
                title={isFloraOpen ? 'Close Flora' : 'Ask Flora'}
                aria-expanded={isFloraOpen}
                aria-controls="flora-page-chat-panel"
                data-active={isFloraOpen}
                onClick={() => setIsFloraOpen((open) => !open)}
              >
                <img src={icons.iconFlora} alt="" width={22} height={22} aria-hidden="true" />
              </FloraFab>
            )}
          </TopRightControls>
        )}
        <FloraPageEmbed ref={embedRef} pageId={pageId} />
      </PagePane>
      {hasOpenedFlora && useAI && (
        <FloraPane id="flora-page-chat-panel" hidden={!isFloraOpen} style={{ width: floraPaneWidth }}>
          <FloraResizeHandle
            edge="left"
            onMouseDown={startFloraResize}
            isDragging={isResizingFlora}
            onResize={(deltaX) => setFloraPaneWidth((previousWidth) => previousWidth + deltaX)}
            width={floraPaneWidth}
            minWidth={FLORA_ASIDE_MIN_WIDTH}
            maxWidth={floraPaneMaxWidth}
          />
          <FloraAside
            isPageView
            contextPreamble={floraContextPreamble}
            contextLabel={page?.title}
            onClose={() => setIsFloraOpen(false)}
          />
        </FloraPane>
      )}
      {/* While dragging, cover the page so the sandboxed iframe can't swallow the
          mousemove/mouseup — otherwise the resize stalls the moment the cursor
          crosses onto the page and the drag never releases. */}
      {isResizingFlora && <ResizeShield />}
    </FullWindow>
  );
}

const FullWindow = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  background: var(--base-white);
`;

/* Transparent capture layer shown only during a resize drag, above the iframe so
   pointer events reach the window listeners that drive the resize. */
const ResizeShield = styled.div`
  position: fixed;
  inset: 0;
  z-index: 150;
  cursor: col-resize;
`;

const FloraPane = styled.div`
  position: relative;
  flex: 0 0 auto;
  height: 100%;
  border-left: 1px solid var(--sky-light);

  &[hidden] {
    display: none;
  }
`;

const PagePane = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

const FloatingToolbar = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 101;
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
  opacity: 0.55;
  transition: opacity 120ms ease;

  &:hover {
    opacity: 1;
  }
`;

const TopRightControls = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 101;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* The owner-only visibility control keeps the subtle, fade-until-hover chrome. */
const VisibilityPill = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
  opacity: 0.55;
  transition: opacity 120ms ease;

  &:hover {
    opacity: 1;
  }
`;

/* The Flora toggle stands apart from the faded chrome: a full-opacity, accented
   button so it always reads as the primary action, and an active state while the
   panel is open. */
const FloraFab = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid var(--secondary-light);
  background: var(--base-white);
  box-shadow: 0 2px 10px rgb(0 0 0 / 14%);
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

  &:hover {
    background: var(--secondary-lightest);
    border-color: var(--secondary-base);
    box-shadow: 0 3px 14px rgb(0 0 0 / 18%);
  }

  &[data-active='true'] {
    background: var(--secondary-lightest);
    border-color: var(--secondary-base);
  }
`;

const ToolbarButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 6px;

  &:hover {
    background: var(--sky-lightest);
  }
`;
